import { memo, useState } from 'react';
import { Typography, Box, useTheme, Tooltip } from '@mui/material';
import { IconHelper } from 'components/generic/IconHelper';
import {
  cleanStringFromHtmlAndBloxInline,
  isAssetTypeEditorEnabled,
  formatAssetString,
} from 'utility/functions';
import { SearchContextType } from 'components/Search/types';
import SearchMediaPreview from './SearchMediaPreview';
import { GenericSearchItem } from '../../SearchItemsProvider';
import {
  EditorialAssetSearchResultItem,
  WireSearchResultItem,
} from 'utility/types/dataProvider';
import { OneOf } from 'utility/types/dataProvider';
import { useTranslation } from 'react-i18next';

export const summaryCardHeight = 100;
export const summaryCardHeightPx = `${summaryCardHeight}px`;

// todo: make separate components/prop-configs for wire, editorial, etc...
// so if the data shapes are not the same then this is easier to manage
type PossibleRecord = OneOf<
  [GenericSearchItem, WireSearchResultItem, EditorialAssetSearchResultItem]
>;

type SummaryCardProps = {
  record: PossibleRecord;
  previewPosition?: 'left' | 'right';
  showEmptyPreview?: boolean;
  previewWidth?: string;
  previewHeight?: string;
  view: SearchContextType['view'];
};

const SummaryCard = memo(
  ({
    record,
    previewPosition = 'left',
    showEmptyPreview = true,
    previewWidth = '172px',
    previewHeight = summaryCardHeightPx,
    view,
  }: SummaryCardProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    // hover states for styles
    const [cardHover, setCardHover] = useState<boolean>(false);
    const isDisabled =
      record.status === 'deleted' || !isAssetTypeEditorEnabled(record.type);
    const summary = record?.summary;
    const content = record?.content;
    const previews = record?.previews;
    // image_preview is syndication, previews is editorial
    const image_preview = record?.image_preview;
    const previewUrl =
      image_preview?.url || previews?.thumbnail?.url || previews?.size1?.url;
    const baseSummary = summary || content || '';
    const assetTypeStr = formatAssetString(record?.type, t);
    const cleanedSummary = cleanStringFromHtmlAndBloxInline(baseSummary);
    const truncateStyle = {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };

    const gridTemplateColumns =
      showEmptyPreview || previewUrl
        ? previewPosition === 'left'
          ? `${previewWidth} 1fr`
          : `1fr ${previewWidth}`
        : '1fr';

    return (
      <Box
        id="summary-card"
        data-testid="summary-card"
        sx={{
          display: 'grid',
          gridTemplateColumns: gridTemplateColumns,
          alignItems: 'center',
          gap: 1,
          maxWidth: '100%',
          height: summaryCardHeightPx,
          maxHeight: summaryCardHeightPx,
          // Ref: SearchMediaPreview.
          '& .video-expand-icon-overlay': {
            opacity: 0,
          },
          '&:hover': {
            '& .play-icon-overlay': {
              opacity: 0,
            },
            '& .video-expand-icon-overlay': {
              opacity: 1,
            },
          },
        }}
        onMouseEnter={() => {
          setCardHover(true);
        }}
        onMouseLeave={() => {
          setCardHover(false);
        }}
      >
        {previewPosition === 'left' && (showEmptyPreview || previewUrl) && (
          <SearchMediaPreview
            view={view}
            record={record as GenericSearchItem}
            previewImageUrl={previewUrl}
            previewHeight={previewHeight}
            previewWidth={previewWidth}
            isHovered={cardHover}
          />
        )}
        <Box
          data-testid="summary-card-title-type"
          sx={{
            color: isDisabled ? theme.palette.text.disabled : theme.palette.text.primary,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 0.25,
              mb: 1,
            }}
          >
            <Tooltip
              data-testid="summary-card-type-tooltip"
              title={assetTypeStr}
              placement="top"
            >
              <Box sx={{ mr: 0.5 }}>
                <IconHelper
                  iconType={record.type}
                  svgIconProps={{ fontSize: 'small' }}
                />
              </Box>
            </Tooltip>

            {/* Title. */}
            <Typography
              variant="h2"
              sx={truncateStyle}
            >
              {record.title}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            gutterBottom
            sx={{
              display: 'block',
              ...truncateStyle,
            }}
          >
            {cleanedSummary}
          </Typography>
        </Box>
        {previewPosition === 'right' && (showEmptyPreview || previewUrl) && (
          <SearchMediaPreview
            view={view}
            record={record as GenericSearchItem}
            previewHeight={previewHeight}
            previewWidth={previewWidth}
            isHovered={cardHover}
          />
        )}
      </Box>
    );
  },
);

SummaryCard.displayName = 'SummaryCard';
export default SummaryCard;
